import { Link } from 'react-router-dom';
import { ANDROID_APP_URL, IOS_APP_URL } from '@/constants/config';
import { useAuth } from '@/context/AuthContext';
import { useHasShops } from '@/hooks/useAppData';
import { useHasSaleEvents } from '@/hooks/useSaleCountries';
import { useTopCategories } from '@/hooks/useCategories';
import { useNavCities } from '@/hooks/useNavCities';
import { useCatalogPath, useRouteLocale } from '@/hooks/useRouteLocale';
import { GeoNamesAttribution } from '@/components/legal/GeoNamesAttribution';
import { StoreBadge } from '@/components/ui';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { linkedFromHubs, sitePath } from '@/seo/hubLinks';
import { liveTrustPages } from '@/seo/trustPages';
import { localePath } from '@/seo/urls';
import { SiteSocialLinks } from './SiteSocialLinks';

const currentYear = new Date().getFullYear();

/**
 * The trust pages the Company column links (web plan W3.7): only the live
 * ones — a page whose owner copy is still empty does not exist (it is a 404),
 * so it is never linked. Owner content is fixed at build time.
 */
const LIVE_TRUST_PAGES = liveTrustPages();

// `catalog`: an un-prefixed catalog path, linked through `useCatalogPath`
// (`/pk/en/…` once the URL move is live, web plan W2.1).
const EXPLORE_LINKS: { to: string; label: string; catalog?: boolean; sales?: boolean }[] = [
  { to: '/brands', label: 'Brands', catalog: true },
  { to: '/shops', label: 'Shops', catalog: true },
  { to: '/promotions', label: 'Promotions', catalog: true },
  { to: '/search', label: 'Search', catalog: true },
  { to: '/nearby', label: 'Nearby', catalog: true },
  // The sale event hubs' index (web plan W0.15), linked from every page.
  // `saleCalendarPath()` in @/seo/saleEvents builds the same path; importing
  // that module here would put the whole event config and its date helpers
  // in the entry chunk (navigation.test.tsx keeps the two equal).
  // Only where the route's country has sale events (`useHasSaleEvents`, backend `locales { hasSaleEvents }`); `sales`: linked under the page's locale.
  { to: '/sales', label: 'Sale calendar', sales: true },
];

/** At most this many category hubs in "Top categories". */
const TOP_CATEGORY_LINKS = 8;

/**
 * md and up: one column per block, four per row; from lg the extra blocks
 * (Account for a signed-in user, Top categories once a hub is indexable) join
 * the row. Literal class names, so Tailwind generates them.
 */
const GRID_COLUMNS: Readonly<Record<number, string>> = {
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-4 lg:grid-cols-5',
  6: 'md:grid-cols-4 lg:grid-cols-6',
  7: 'md:grid-cols-4 lg:grid-cols-7',
};

const ACCOUNT_LINKS = [
  { to: '/profile', label: 'Profile' },
  { to: '/favorites', label: 'Favorites' },
  { to: '/notifications', label: 'Notifications' },
  { to: '/settings', label: 'Settings' },
  { to: '/feedback', label: 'Send feedback' },
];

/*
 * The footer's column headings are H2s: they follow whatever the page ends
 * with, and on a page whose content has no H2 (the /brands list: H1, then
 * cards) an H3 here skipped a level (Lighthouse heading-order).
 */
function FooterLinks({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-bold text-ink">{title}</h2>
      <ul className="space-y-0.5">
        {links.map((link) => (
          <li key={link.to}>
            {/* py-1 makes the tap target taller than the text on touch screens. */}
            <Link
              to={link.to}
              className="inline-block py-1 text-sm text-ink-secondary transition-colors duration-200 hover:text-primary-strong"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const { user } = useAuth();
  const hasShops = useHasShops();
  const catalog = useCatalogPath();
  const locale = useRouteLocale();
  const saleEvents = useHasSaleEvents(locale);
  const t = useTranslate();
  const { items: topCategories } = useTopCategories();
  const { items: navCities } = useNavCities();
  // `/shops` is linked only while it has shops to list (web plan W0.3).
  const exploreLinks = EXPLORE_LINKS.filter(
    (link) => (hasShops || link.to !== '/shops') && (!link.sales || saleEvents),
  ).map((link) =>
    link.catalog
      ? { to: catalog(link.to), label: link.label }
      : link.sales
        ? { to: localePath(locale, link.to), label: link.label }
        : { to: link.to, label: link.label },
  );
  // Top categories (web plan W3.1/W3.7): only the level-1 hubs the backend
  // lists (`seo.indexable`, master D-5: a hub under its threshold, or one the
  // backend doesn't list yet, is never linked from every page).
  const topCategoryLinks = topCategories
    .filter((category) => linkedFromHubs(category) && sitePath(category.urlPath))
    .slice(0, TOP_CATEGORY_LINKS)
    .map((category) => ({ to: sitePath(category.urlPath) as string, label: category.title }));
  // City hubs (web plan W3.2): indexable cities with stores.
  const cityLinks = navCities.map((city) => ({ to: sitePath(city.urlPath) as string, label: city.name }));
  // Company (web plan W3.7): the live trust pages, then the legal pages
  // (global URLs, never under a locale prefix).
  const companyLinks = [
    ...LIVE_TRUST_PAGES.map((page) => ({ to: page.path, label: t(`common:footer.${page.key}`) })),
    { to: '/privacy', label: t('common:footer.privacy') },
    { to: '/terms', label: t('common:footer.terms') },
  ];

  return (
    <footer className="mt-16 border-t border-line bg-surface">
      <div className="mx-auto max-w-7xl px-4">
        {/* Phones: brand and app blocks span the full width, the link lists
            sit side by side. md and up: one column per block — four for a
            guest (brand, Explore, Company, app), who gets no Account column;
            signed in, the fifth wraps below md's four and joins them from lg. */}
        <div
          className={`grid grid-cols-2 gap-x-6 gap-y-8 py-10 md:gap-8 md:py-12 ${
            GRID_COLUMNS[
              4 + (user ? 1 : 0) + (topCategoryLinks.length > 0 ? 1 : 0) + (cityLinks.length > 0 ? 1 : 0)
            ]
          }`}
        >
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to={catalog('/')} className="flex items-center gap-2" aria-label="PromoStork home">
              <img src="/logo-72.jpg" alt="" width="36" height="36" decoding="async" className="h-9 w-9 rounded-xl object-cover" />
              <span className="text-lg font-extrabold tracking-tight">
                <span className="text-ink">Promo</span>
                <span className="text-primary-strong">Stork</span>
              </span>
            </Link>
            <p className="mt-3 max-w-xs text-sm text-ink-secondary">
              Discover promotions, deals and offers from brands and shops around you.
            </p>
            {/* PromoStork's own profiles (owner content): nothing until confirmed. */}
            <SiteSocialLinks className="mt-4" />
          </div>

          <FooterLinks title="Explore" links={exploreLinks} />
          {/* Signed-in users only (web plan W0.3): for guests and crawlers these
              are noindex pages, mostly sign-in prompts, that would otherwise be
              linked from every page. Guests still reach "Send feedback" from the
              header menu and the drawer. */}
          {topCategoryLinks.length > 0 && (
            <FooterLinks title={t('common:footer.topCategories')} links={topCategoryLinks} />
          )}
          {cityLinks.length > 0 && (
            <FooterLinks title={t('common:footer.dealsByCity')} links={cityLinks} />
          )}
          {user && <FooterLinks title="Account" links={ACCOUNT_LINKS} />}
          <FooterLinks title={t('common:footer.company')} links={companyLinks} />

          {/* Get the app — the App Store badge appears once IOS_APP_URL is set.
              A card on phones, where it would otherwise trail off the end of a
              long stack; a plain column from md up. */}
          <div className="col-span-2 rounded-2xl border border-line bg-card p-4 md:col-span-1 md:rounded-none md:border-0 md:bg-transparent md:p-0">
            <h2 className="mb-2 text-sm font-bold text-ink md:mb-3">Get the app</h2>
            <p className="text-sm text-ink-secondary">
              Take your deals with you — follow brands and get notified the moment a new promotion
              drops.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {IOS_APP_URL && <StoreBadge store="app-store" href={IOS_APP_URL} />}
              <StoreBadge store="google-play" href={ANDROID_APP_URL} />
            </div>
          </div>
        </div>

        {/* Bottom bar. The legal links moved into the Company column (web
            plan W3.7). */}
        <div className="border-t border-line-light py-4 text-xs text-ink-secondary">
          <p>&copy; {currentYear} PromoStork</p>
        </div>

        {/* The GeoNames licence attribution (web plan W0.19), on every page. */}
        <GeoNamesAttribution className="pb-4 text-xs text-ink-secondary" />
      </div>
    </footer>
  );
}

export default Footer;
